import { useEffect, useState } from "react";
import { Plus, Pencil, Copy, Trash2, Eye, ArrowLeft, Save, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Btn, Field, Area, Select, StatusBadge } from "@/components/admin/ui";
import RichTextEditor from "@/components/editor/RichTextEditor";
import MediaPicker from "@/components/admin/MediaPicker";

const BLANK = { title: "", slug: "", excerpt: "", featured_image: "", categories: [], tags: [], author: "Healthy Roots", status: "draft", content: "<p></p>", seo: {} };

export default function Blog() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [picker, setPicker] = useState(false);
  const load = () => api.get("/admin/blog").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    const body = { ...editing, categories: csv(editing.categories), tags: csv(editing.tags) };
    try {
      if (editing.id) await api.put(`/admin/blog/${editing.id}`, body);
      else await api.post("/admin/blog", body);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { if (!window.confirm("Delete this article?")) return; await api.delete(`/admin/blog/${id}`); toast.success("Deleted"); load(); };
  const dup = async (id) => { await api.post(`/admin/blog/${id}/duplicate`); toast.success("Duplicated"); load(); };

  if (editing) return (
    <div data-testid="blog-editor">
      <PageHeader title={editing.id ? "Edit Article" : "New Article"}>
        <Btn variant="outline" onClick={() => setEditing(null)}><ArrowLeft size={16} /> Back</Btn>
        <Btn onClick={save} data-testid="save-blog-btn"><Save size={16} /> Save</Btn>
      </PageHeader>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4">
          <Field label="Title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
          <Area label="Excerpt" value={editing.excerpt} onChange={(v) => setEditing({ ...editing, excerpt: v })} rows={2} />
          <div><span className="text-sm font-medium text-emerald-900">Content</span><div className="mt-1"><RichTextEditor value={editing.content} onChange={(v) => setEditing({ ...editing, content: v })} /></div></div>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-stone-200/80 space-y-4 h-fit">
          <Select label="Status" value={editing.status} onChange={(v) => setEditing({ ...editing, status: v })} options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]} />
          <Field label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} />
          <div><span className="text-sm font-medium text-emerald-900">Featured image</span>
            <div className="flex gap-2 mt-1"><input value={editing.featured_image || ""} onChange={(e) => setEditing({ ...editing, featured_image: e.target.value })} className="flex-1 rounded-lg border border-stone-300 px-3 py-2 text-sm outline-none" /><button onClick={() => setPicker(true)} className="rounded-lg border border-stone-300 px-3 py-2 hover:bg-stone-100"><ImageIcon size={15} /></button></div>
            {editing.featured_image && <img src={editing.featured_image} alt="" className="mt-2 h-24 w-full rounded-lg object-cover border" />}
          </div>
          <Field label="Categories (comma separated)" value={arr(editing.categories)} onChange={(v) => setEditing({ ...editing, categories: v })} />
          <Field label="Tags (comma separated)" value={arr(editing.tags)} onChange={(v) => setEditing({ ...editing, tags: v })} />
          <Field label="Author" value={editing.author} onChange={(v) => setEditing({ ...editing, author: v })} />
          <Area label="SEO description" value={editing.seo?.description} onChange={(v) => setEditing({ ...editing, seo: { ...editing.seo, description: v } })} rows={2} />
        </div>
      </div>
      <MediaPicker open={picker} onClose={() => setPicker(false)} onSelect={(url) => setEditing({ ...editing, featured_image: url })} />
    </div>
  );

  return (
    <div data-testid="admin-blog">
      <PageHeader title="Blog" subtitle="Write and publish articles">
        <Btn onClick={() => setEditing({ ...BLANK })} data-testid="new-blog-btn"><Plus size={16} /> New Article</Btn>
      </PageHeader>
      <ContentTable items={items} onEdit={setEditing} onDel={del} onDup={dup} prefix="blog" />
    </div>
  );
}

export function ContentTable({ items, onEdit, onDel, onDup, prefix }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200/80 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 text-stone-500 text-left"><tr><th className="px-5 py-3 font-medium">Title</th><th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3 font-medium">Updated</th><th className="px-5 py-3 font-medium text-right">Actions</th></tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={4} className="text-center text-stone-400 py-10">Nothing here yet.</td></tr>}
          {items.map((p) => (
            <tr key={p.id} className="border-t border-stone-100" data-testid={`${prefix}-row-${p.slug}`}>
              <td className="px-5 py-3 font-medium text-emerald-900">{p.title}{p.review && <span className="ml-2 text-[10px] uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">review</span>}</td>
              <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
              <td className="px-5 py-3 text-xs text-stone-500 font-mono">{(p.updated_at || "").slice(0, 10)}</td>
              <td className="px-5 py-3"><div className="flex justify-end gap-1">
                {p.status === "published" && <a href={`/${prefix}/${p.slug}`} target="_blank" rel="noreferrer" className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><Eye size={16} /></a>}
                <button onClick={() => onEdit(p)} className="p-2 rounded-lg hover:bg-stone-100 text-emerald-700"><Pencil size={16} /></button>
                <button onClick={() => onDup(p.id)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500"><Copy size={16} /></button>
                <button onClick={() => onDel(p.id)} className="p-2 rounded-lg hover:bg-red-50 text-red-500"><Trash2 size={16} /></button>
              </div></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export const csv = (v) => Array.isArray(v) ? v : (v || "").split(",").map((s) => s.trim()).filter(Boolean);
export const arr = (v) => Array.isArray(v) ? v.join(", ") : (v || "");
